import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceAPI } from '../services/api';
import '../styles/Payment.css';

const Wallet = () => {
  const { t } = useTranslation();
  const [balance, setBalance] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('balance');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [balanceRes, salesRes] = await Promise.all([
        marketplaceAPI.getWalletBalance(),
        marketplaceAPI.getSalesHistory()
      ]);
      setBalance(balanceRes.data);
      setSales(salesRes.data);
    } catch (err) {
      setError(t('wallet.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    'pending_payment': t('status.pendingPayment'),
    'paid': t('status.soldPending'),
    'shipped': t('status.shipped'),
    'delivered': t('status.deliveredPending'),
    'completed': t('status.completed'),
    'cancelled': t('status.cancelled'),
    'refunded': t('status.refunded')
  };

  const statusColors = {
    'pending_payment': '#ffc107',
    'paid': '#17a2b8',
    'shipped': '#6f42c1',
    'delivered': '#fd7e14',
    'completed': '#28a745',
    'cancelled': '#6c757d',
    'refunded': '#dc3545'
  };

  if (loading) {
    return (
      <div className="payment-container">
        <div className="payment-card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-container wallet-page">
      <div className="payment-card">
        <h2>{t('wallet.title')}</h2>

        {/* Solde */}
        <div className="wallet-balance-card">
          <div className="balance-amount">
            <span className="currency">CHF</span>
            <span className="amount">{balance?.walletBalance?.toFixed(2) || '0.00'}</span>
          </div>
          <p className="balance-label">{t('wallet.totalEarnings')}</p>

          {balance?.stripeBalance && (
            <div className="stripe-balance">
              <div className="balance-row">
                <span>{t('wallet.available')}</span>
                <span>{balance.stripeBalance.available?.toFixed(2)} CHF</span>
              </div>
              <div className="balance-row">
                <span>{t('wallet.pending')}</span>
                <span>{balance.stripeBalance.pending?.toFixed(2)} CHF</span>
              </div>
            </div>
          )}
        </div>

        {/* Info sur les virements */}
        <div className="info-box">
          <span className="info-icon">&#128712;</span>
          <p>
            {t('wallet.payoutInfo')}
          </p>
        </div>

        {/* Tabs */}
        <div className="wallet-tabs">
          <button
            className={`tab-btn ${activeTab === 'balance' ? 'active' : ''}`}
            onClick={() => setActiveTab('balance')}
          >
            {t('wallet.mySales')}
          </button>
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            {t('wallet.ongoing')}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Liste des ventes */}
        <div className="sales-list">
          {sales.length === 0 ? (
            <div className="empty-state">
              <p>{t('wallet.noSales')}</p>
              <button className="btn-primary" onClick={() => navigate('/create-product')}>
                {t('wallet.sellItem')}
              </button>
            </div>
          ) : (
            sales
              .filter(sale => {
                if (sale.status === 'pending_payment') return false;
                if (activeTab === 'pending') {
                  return ['paid', 'shipped', 'delivered'].includes(sale.status);
                }
                return true;
              })
              .map(sale => {
                let shippingAddress = null;
                if (sale.shipping_address) {
                  try {
                    shippingAddress = typeof sale.shipping_address === 'string'
                      ? JSON.parse(sale.shipping_address)
                      : sale.shipping_address;
                  } catch (e) {}
                }

                return (
                  <div key={sale.id} className="sale-item sale-item-detailed">
                    <div className="sale-header">
                      <img
                        src={sale.product_image
                          ? `${API_URL.replace('/api', '')}/uploads/thumbnails/${sale.product_image}`
                          : 'https://via.placeholder.com/60x60?text=?'
                        }
                        alt={sale.product_title}
                        className="sale-image"
                      />
                      <div className="sale-info">
                        <h4>{sale.product_title}</h4>
                        <p className="buyer-name">{t('movements.buyerLabel', { name: sale.buyer_name })}</p>
                        <span
                          className="sale-status"
                          style={{ backgroundColor: statusColors[sale.status] }}
                        >
                          {statusLabels[sale.status]}
                        </span>
                      </div>
                      <div className="sale-amount">
                        <span className="earnings">+{sale.seller_earnings?.toFixed(2)} CHF</span>
                        {sale.status === 'completed' && (
                          <span className="paid-badge">{t('wallet.paidBadge')}</span>
                        )}
                      </div>
                    </div>

                    {/* Détails de livraison */}
                    {sale.status !== 'pending_payment' && (
                      <div className="sale-details">
                        <div className="sale-detail-row">
                          <span className="detail-label">
                            {sale.shipping_method === 'shipping' ? '\u{1F4E6}' : '\u{1F4CD}'} {t('movements.shippingMode')}
                          </span>
                          <span className="detail-value">
                            {sale.shipping_method === 'shipping' ? t('movements.postalShipping') : t('movements.handDelivery')}
                          </span>
                        </div>

                        {sale.shipping_method === 'shipping' && shippingAddress && shippingAddress.name && (
                          <div className="sale-detail-row">
                            <span className="detail-label">{t('movements.addressLabel')}</span>
                            <span className="detail-value">
                              {shippingAddress.name}, {shippingAddress.street}, {shippingAddress.postalCode} {shippingAddress.city}
                            </span>
                          </div>
                        )}

                        {sale.buyer_message && (
                          <div className="sale-detail-row buyer-message-row">
                            <span className="detail-label">{'\u{1F4AC}'} {t('movements.messageLabel')}</span>
                            <span className="detail-value buyer-message-text">"{sale.buyer_message}"</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>

        {/* Actions */}
        <div className="wallet-actions">
          <button className="btn-secondary" onClick={() => navigate('/seller/onboarding')}>
            {t('wallet.accountSettings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Wallet;
